<script setup>
const props = defineProps({
  referenceRef: {
    type: [Object, null],
    required: true,
  },

  show: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: undefined,
  },
  text: {
    type: String,
    default: undefined,
  },

  gap: {
    type: Number,
    default: 33,
  },
  gapSm: {
    type: Number,
    default: 21,
  },

  padding: {
    type: Number,
    default: 23,
  },
  paddingSm: {
    type: Number,
    default: 11,
  },

  size: {
    type: String,
    default: undefined,
    validator(size) {
      return ['small'].includes(size);
    },
  },
  titleSize: {
    type: String,
    default: undefined,
    validator(titleSize) {
      return ['small'].includes(titleSize);
    },
  },

  sameWidth: {
    type: Boolean,
    default: false,
  },
});

const arrowRef = ref(null);

const breakpoint = useCustomBreakpoints();
const isSmBreakpoint = breakpoint.smaller('sm');

// gap
const gapDesktop = useResizedPx(props.gap);
const gapMobile = useResizedPx(props.gapSm);
const gapValue = computed(() =>
  isSmBreakpoint.value ? gapMobile.value : gapDesktop.value,
);

// padding
const paddingDesktop = useResizedPx(props.padding);
const paddingMobile = useResizedPx(props.paddingSm);
const paddingValue = computed(() =>
  isSmBreakpoint.value ? paddingMobile.value : paddingDesktop.value,
);
</script>

<template>
  <LazyCFloating
    v-if="show"
    v-slot="{ arrowStyles }"
    class="ui-tooltip"
    :class="{ [`ui-tooltip--size--${size}`]: size }"
    :reference-ref="referenceRef"
    position="top-center"
    :gap="gapValue"
    :padding="paddingValue"
    :arrow-ref="arrowRef"
    :same-width="sameWidth"
  >
    <span
      v-if="title"
      class="ui-tooltip__font ui-tooltip__font--title"
      :class="{ [`ui-tooltip__font--title-size--${titleSize}`]: titleSize }"
    >
      {{ $tp(title) }}
    </span>

    <UiHtmlContent
      v-if="text"
      class="ui-tooltip__font ui-tooltip__font--text"
      :content="text"
      custom-font
    />

    <div ref="arrowRef" class="ui-tooltip__arrow" :style="arrowStyles" />
  </LazyCFloating>
</template>

<style scoped lang="scss">
.ui-tooltip {
  $parent: &;

  border: 1px solid $color-gray-1;
  padding: em(40) em(30);
  background-color: $color-white;
  display: flex;
  flex-direction: column;
  gap: em(34);

  @include media-breakpoint-down(sm) {
    padding: em(20);
    gap: em(22);
  }

  &__font {
    &--title {
      font-size: em(32);
      line-height: em(35, 32);

      @include media-breakpoint-down(sm) {
        font-size: em(20);
        line-height: em(22, 20);
      }
    }

    &--title-size {
      &--small {
        font-size: em(20);
        line-height: em(22, 20);
      }
    }

    &--text {
      font-size: em(18);
      line-height: em(20);
      font-weight: 250;

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(18, 16);
      }
    }
  }

  &__arrow {
    position: absolute;
    width: em(20);
    height: em(20);
    background-color: $color-white;
    transform: rotate(45deg);
    border: 1px solid $color-gray-1;
    border-left: none;
    border-top: none;
  }

  &--size {
    &--small {
      padding: em(15);
      gap: em(10);

      #{$parent} {
        &__font {
          &--title {
            font-size: em(18);
            line-height: em(20);

            @include media-breakpoint-down(sm) {
              font-size: em(16);
              line-height: em(18, 16);
            }
          }

          &--text {
            font-size: em(16);
            line-height: em(18, 16);
          }
        }
      }
    }
  }
}
</style>
